/**
 * Phase 9.6 Batch 3 — 🛡️ Admin Media Moderation
 * ----------------------------------------------
 * Founder/admin-only tool to:
 *   • Find a user (by user_id or artist_name)
 *   • View their current profile photo + banner
 *   • Replace either by file upload or by direct URL
 *   • Reset (clear) either back to defaults
 *   • Review the most recent media moderation actions for that user
 *
 * All actions are logged server-side in admin_media_logs.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { getProfilePhotoUrl, getBannerUrl } from "@/lib/mediaUrls";
import { useRequireAdmin } from "@/lib/useRequireAdmin";
import SharedNavigation from "@/components/SharedNavigation";

const AdminMediaPage = () => {
  const navigate = useNavigate();
  const { authChecked } = useRequireAdmin();
  const [denied, setDenied] = useState(false);
  const [checking, setChecking] = useState(true);
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recentLogs, setRecentLogs] = useState([]);

  // Probe admin access on mount via the media-logs endpoint (admin-only).
  useEffect(() => {
    if (!authChecked) return;
    (async () => {
      try {
        const r = await api.get("/admin/media-logs?limit=10");
        setRecentLogs(r.data?.items || []);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) setDenied(true);
      } finally {
        setChecking(false);
      }
    })();
  }, [authChecked]);

  const lookup = async () => {
    const q = (query || "").trim();
    if (!q) { toast.error("Enter a user_id, name, email, or artist name"); return; }
    setBusy(true);
    try {
      // Phase 12.1 — use the new admin user-search endpoint which
      // performs case-insensitive regex across name + email +
      // artist_name + user_id. Fixes "User not found" for legit artists.
      let user = null;
      try {
        const s = await api.get(`/admin/users/search`, {
          params: { q, limit: 10 },
        });
        const results = s.data?.results || [];
        // Prefer exact-ish matches: user_id, then email, then name, then artist_name.
        const lower = q.toLowerCase();
        user =
          results.find((u) => (u.user_id || "").toLowerCase() === lower) ||
          results.find((u) => (u.email || "").toLowerCase() === lower) ||
          results.find((u) => (u.name || "").toLowerCase() === lower) ||
          results.find((u) => (u.artist_name || "").toLowerCase() === lower) ||
          results[0] || null;
      } catch (_e) {
        // Endpoint missing (older deploy) — fall back to the legacy path.
        try {
          const r = await api.get(`/artist/${q}`);
          user = r.data?.user;
        } catch (_e2) { /* fall through */ }
      }

      if (!user) { toast.error("User not found"); setTarget(null); return; }

      // Hydrate the full artist record (banner, badges, etc.) so the
      // moderation UI has everything it needs.
      try {
        const r = await api.get(`/artist/${user.user_id}`);
        user = r.data?.user || user;
      } catch { /* keep partial */ }

      setTarget(user);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally { setBusy(false); }
  };

  const reload = async () => {
    if (!target) return;
    const r = await api.get(`/artist/${target.user_id}`);
    setTarget(r.data?.user);
    const logs = await api.get(`/admin/media-logs?user_id=${target.user_id}&limit=10`);
    setRecentLogs(logs.data?.items || []);
  };

  const uploadFile = async (kind, file) => {
    if (!target || !file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/users/${target.user_id}/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind} updated`);
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally { setBusy(false); }
  };

  const setByUrl = async (kind) => {
    const url = window.prompt(`New ${kind === "profile-photo" ? "profile photo" : "banner"} URL:`);
    if (!url) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.user_id}/${kind}`, { url });
      toast.success(`${kind} updated`);
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    } finally { setBusy(false); }
  };

  const reset = async (kind) => {
    if (!window.confirm(`Reset ${kind} to default? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${target.user_id}/${kind}`);
      toast.success(`${kind} reset`);
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reset failed");
    } finally { setBusy(false); }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a0514] text-white flex items-center justify-center p-6" data-testid="admin-media-checking">
        <p className="text-pink-200/70 text-sm">Checking admin access…</p>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="min-h-screen bg-[#0a0514] text-white flex items-center justify-center p-6" data-testid="admin-media-denied">
        <Card className="max-w-md p-6 bg-rose-500/10 border-rose-400/40 text-center">
          <h1 className="text-2xl font-black text-white mb-2">🔒 Admin only</h1>
          <p className="text-rose-200/80 text-sm">Founder/admin badge required.</p>
          <Button onClick={() => navigate("/")} data-testid="admin-media-go-home" className="mt-4 bg-rose-600 hover:bg-rose-500">Go Home</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
      <SharedNavigation />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="admin-media-page">
        <div className="flex items-center gap-2 mb-3">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="text-white border-white/20 hover:bg-white/5"
            data-testid="admin-media-back-btn"
          >
            ← Back
          </Button>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold mb-2">🛡️ Admin Media Moderation</h1>
        <p className="text-pink-200/80 text-sm mb-6">
          Replace, reset, or moderate any user's profile photo and banner. All actions are logged.
        </p>

        <Card className="p-4 mb-5 bg-black/30 border-pink-500/20">
          <Label className="text-xs text-pink-200">Find user (user_id or artist name)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="user_xxxx, name, email, or @artist_name"
              className="bg-black/40 border-pink-500/30 text-white"
              data-testid="admin-media-search"
            />
            <Button
              onClick={lookup}
              disabled={busy}
              className="bg-gradient-to-r from-pink-500 to-purple-600"
              data-testid="admin-media-search-btn"
            >
              Find
            </Button>
          </div>
        </Card>

        {target && (
          <Card className="p-5 mb-5 bg-gradient-to-br from-pink-500/10 to-purple-500/10 border-pink-500/30" data-testid="admin-media-target">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={getProfilePhotoUrl(target)}
                alt={target.artist_name}
                className="w-14 h-14 rounded-full object-cover border-2 border-pink-500/40"
              />
              <div>
                <p className="text-lg font-bold text-white">{target.artist_name || target.name}</p>
                <p className="text-xs text-pink-200/70">{target.user_id}</p>
              </div>
            </div>

            {/* Profile photo */}
            <div className="mb-4 pb-4 border-b border-white/10">
              <p className="text-xs uppercase tracking-wider text-pink-200/70 font-bold mb-2">Profile Photo</p>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  type="file" accept="image/*"
                  onChange={(e) => e.target.files?.[0] && uploadFile("profile-photo", e.target.files[0])}
                  data-testid="admin-media-photo-upload"
                  className="text-xs"
                />
                <Button size="sm" variant="outline" onClick={() => setByUrl("profile-photo")}
                  data-testid="admin-media-photo-url-btn"
                  className="border-pink-500/40 text-pink-200">Set by URL</Button>
                <Button size="sm" variant="outline" onClick={() => reset("profile-photo")}
                  data-testid="admin-media-photo-reset-btn"
                  className="border-rose-500/40 text-rose-200">🗑️ Reset</Button>
              </div>
            </div>

            {/* Banner */}
            <div>
              <p className="text-xs uppercase tracking-wider text-pink-200/70 font-bold mb-2">Banner</p>
              <div className="aspect-[4/1] bg-black/40 rounded-lg overflow-hidden mb-2 border border-pink-500/20">
                {getBannerUrl(target) && <img src={getBannerUrl(target)} alt="banner" className="w-full h-full object-cover" />}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  type="file" accept="image/*"
                  onChange={(e) => e.target.files?.[0] && uploadFile("banner", e.target.files[0])}
                  data-testid="admin-media-banner-upload"
                  className="text-xs"
                />
                <Button size="sm" variant="outline" onClick={() => setByUrl("banner")}
                  data-testid="admin-media-banner-url-btn"
                  className="border-pink-500/40 text-pink-200">Set by URL</Button>
                <Button size="sm" variant="outline" onClick={() => reset("banner")}
                  data-testid="admin-media-banner-reset-btn"
                  className="border-rose-500/40 text-rose-200">🗑️ Reset</Button>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-4 bg-black/30 border-pink-500/20" data-testid="admin-media-logs">
          <h2 className="text-lg font-bold mb-3">Recent Media Actions</h2>
          {recentLogs.length === 0 ? (
            <p className="text-pink-200/60 text-sm">No actions yet.</p>
          ) : (
            <div className="space-y-1">
              {recentLogs.map((l) => (
                <div key={l.log_id} className="text-xs text-pink-200/80 flex gap-2 flex-wrap">
                  <span className="text-pink-300/60">{(l.created_at || "").slice(0, 19).replace("T", " ")}</span>
                  <span className="font-bold text-white">{l.kind}</span>
                  <span>{l.action}</span>
                  <span className="text-pink-300/60">on {l.user_id}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminMediaPage;
